import { yupResolver } from '@hookform/resolvers/yup';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { ChequeGiroModel } from 'api-hooks/cheque-giro';
import { NavigationRoutes } from 'common/constants';
import { ApiError, ApiResult } from 'common/repositories/common.model';
import { SeparatorVertical } from 'components/styles';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import Input from 'mantine-components/elements';
import { HalfGridContainer } from 'mantine-components/container/grid-container';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UniskaiFormHeaderRef } from 'templates/form/uniskai-form-header';
import UniskaiFormTemplate from 'templates/form/uniskai-form-template';

import { ChequeGiroFormSchema, ChequeGiroFormType } from './form-type';
import useChequeGiroDefaultValues from '../hooks/use-cheque-giro-default-values';

interface ChequeGiroFormProps {
  query?: UseQueryResult<ApiResult<ChequeGiroModel>, ApiError>;
  onSubmit: (
    input: ChequeGiroFormType,
    form: UseFormReturn<ChequeGiroFormType>,
  ) => Promise<ChequeGiroModel>;
  saveMutation?: UseMutationResult;
}

export default function ChequeGiroForm(props: ChequeGiroFormProps) {
  const { t } = useTranslation();
  const chequeGiro = props.query?.data?.data;
  const defaultValues = useChequeGiroDefaultValues(chequeGiro);
  const { deleteMutate, onDelete } = useDestroyAction('cheque-giros');
  const permission = useGetCrudPermission('cheque-giros');
  const titleRef = React.useRef<UniskaiFormHeaderRef>(null);

  React.useEffect(() => {
    let title = t(`pathname:${NavigationRoutes.ChequeGirosCreate}`);
    if (chequeGiro) {
      title = t(`pathname:${NavigationRoutes.ChequeGirosView}`, {
        extra: chequeGiro.name,
      });
    }
    titleRef.current?.setTitle(title);
  }, [chequeGiro, t]);

  return (
    <UniskaiFormTemplate
      formProps={{
        defaultEditable: !chequeGiro,
        defaultValues,
        resolver: yupResolver(ChequeGiroFormSchema()),
        onSubmit: props.onSubmit as any,
      }}
      customs={[
        {
          type: 'delete',
          loading: deleteMutate.isLoading,
          onClick: () => onDelete(chequeGiro?.id),
          show: permission.delete,
        },
        {
          type: 'edit',
          show: permission.update,
        },
      ]}
      data={chequeGiro}
      query={props.query}
      titleRef={titleRef}
      saveMutation={props.saveMutation}
    >
      <HalfGridContainer>
        <Input
          type="text"
          name="name"
          label={t('common:name')}
          placeholder={t('common:name')}
          required
        />
      </HalfGridContainer>
      <SeparatorVertical $margin={16} />
      <HalfGridContainer>
        <Input
          type="text"
          name="bankCode"
          label={t('bank-account:bank_code')}
          placeholder={t('bank-account:bank_code')}
          required
        />
      </HalfGridContainer>
      <SeparatorVertical $margin={20} />
      <HalfGridContainer>
        <Input type="checkbox" name="isActive" label={t('common:is_active')} />
      </HalfGridContainer>
    </UniskaiFormTemplate>
  );
}
